import { pageOutlineMaxHeadings, pageOutlineMaxLength, pageOutlineMaxParagraphLength, pageTextMaxBlocks } from './constants.js';
import { isStage, virtualPage } from './snippet.js';

/* The one walk over the visitor's page that both readers share: the flat extract the coverage report
 * posts (page-coverage.js) and the outline the chat reads on demand (readPageOutline). Read off the
 * LIVE nodes, never off a clone: innerText is what the browser renders, so a hydrated single-page
 * application, a dismissed cookie layer and a section collapsed behind a tab all report themselves
 * correctly — and a detached copy would silently fall back to textContent and hand the model the hidden
 * half of the page as well. */

const blockSelector = 'h1, h2, h3, h4, p, li, dd, blockquote, figcaption, td, th';

/* The furniture every page of a site repeats, and the one thing never to read: a form. Its labels are
 * furniture too, and what a visitor typed into it is theirs. */
const furnitureSelector = 'nav, header, footer, aside, form, [aria-hidden="true"]';

const headingLevels = { H1: 1, H2: 2, H3: 3, H4: 4 };

/**
 * Collapses a rendered string to one line of single spaces.
 * @param {*} text - Whatever the DOM handed over
 * @returns {string} The cleaned text, or '' for anything unusable
 */
function clean(text) {
    return typeof text === 'string' ? text.trim().replace(/\s+/g, ' ') : '';
}

/**
 * The page's blocks in document order, each already cleaned: the title, the description, and every
 * text block of the main content that is not furniture. Bounded to pageTextMaxBlocks nodes, so the
 * extract of a ten-thousand-row table costs a bounded walk. Empty blocks are dropped here; deduplication
 * and caps are the callers' business, because the two readers cap differently.
 * @returns {{title: string, description: string, blocks: Array<{level: number, text: string}>}}
 *   level is 1–4 for a heading and 0 for everything else
 */
export function readPageBlocks() {
    const meta = document.querySelector('meta[name="description"], meta[property="og:description"]');
    const page = {
        title: clean(document.title),
        description: clean(meta ? meta.getAttribute('content') : ''),
        blocks: []
    };

    const main = document.querySelector('main, [role="main"], article') || document.body;
    if (!main) {
        return page;
    }

    const nodes = main.querySelectorAll(blockSelector);
    const limit = Math.min(nodes.length, pageTextMaxBlocks);

    for (let i = 0; i < limit; i++) {
        const node = nodes[i];
        if (node.closest(furnitureSelector)) {
            continue;
        }

        const text = clean(node.innerText);
        if (text) {
            page.blocks.push({ level: headingLevels[node.tagName] || 0, text: text });
        }
    }

    return page;
}

/**
 * The page as the chat reads it on demand: title and description, then every heading with the first
 * text block under it. A heading's opening is what says what the section is about; everything past it
 * is the visitor's to read and the model's to ask for again another day. Headings are prefixed with
 * their level as Markdown, so a long article keeps its shape in the extract.
 *
 * A stage page of ours (isStage) stands in for one of the provider's pages, and only its content is his:
 * the rebuilt demo website renders his page in main, while the document's title and description name
 * OUR demo ("Deine LeadWin-Demo für …"). So a stage leaves those two out, and the model reads the page
 * the way it would on his own website. A stage that declares no page of his (the outbound demo page
 * without a rebuild, the dashboard's simulated visit) shows none of his content at all, only our own
 * copy around the agent, and answers with an empty outline: the server then withholds the tool rather
 * than hand the model our pitch as the visitor's page.
 * @returns {string} The outline, or '' when the page yields nothing usable
 */
export function readPageOutline() {
    if (isStage && !virtualPage) {
        return '';
    }

    try {
        const page = readPageBlocks();
        const lines = [];
        const seen = {};
        let length = 0;
        let headings = 0;
        let wantParagraph = true;

        /**
         * Adds a line unless it is too short to mean anything or the page already said it: the h1 is
         * often the title again, and a card grid states its labels twice for screen readers.
         * @param {string} text - The cleaned text
         * @param {string} prefix - What the line starts with in the outline
         * @param {number} cap - Where the text is cut, or 0 for no cut
         * @returns {boolean} Whether the line went in
         */
        function push(text, prefix, cap) {
            let value = text;
            if (cap && value.length > cap) {
                value = value.slice(0, cap - 1).trimEnd() + '…';
            }
            if (value.length < 2 || seen[value]) {
                return false;
            }
            seen[value] = true;
            lines.push(prefix + value);
            length += prefix.length + value.length + 1;
            return true;
        }

        if (!isStage) {
            push(page.title, '', 0);
            push(page.description, '', 0);
        }

        for (const block of page.blocks) {
            if (length >= pageOutlineMaxLength) {
                break;
            }
            if (block.level > 0) {
                if (headings >= pageOutlineMaxHeadings) {
                    break;
                }
                if (push(block.text, '#'.repeat(block.level) + ' ', 0)) {
                    headings++;
                    wantParagraph = true;
                }
            } else if (wantParagraph && push(block.text, '', pageOutlineMaxParagraphLength)) {
                wantParagraph = false;
            }
        }

        return lines.join('\n').slice(0, pageOutlineMaxLength);
    } catch {
        /* A widget must never break the page it is a guest on, least of all while reading it. */
        return '';
    }
}
